/**
 * 1.7.9 — the month, three ways (D-1.7.9-6): the requests of the large-print page and of the email to one person.
 * (Copy as text asks nothing of the server: it is made from the events the screen already holds, by the same shared
 * function.) `eventId`: that one event only, from its own page.
 */
import { useQuery } from '@tanstack/react-query';
import type { MonthEmailResult, MonthLang, MonthPerson, MonthSent } from '@shared/event-month';
import { apiFetch } from '@/lib/api';

const monthPath = (m: string, tail: string) => `/api/events/month/${encodeURIComponent(m)}/${tail}`;
const post = <T,>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });
const scope = (eventId?: string | null, upcoming?: boolean) => (eventId ? { eventId } : upcoming ? { upcoming: true } : {});

/** Print: the month (or one event) in large print — a PDF (base64) to open or download. */
export const printLargeReq = (month: string, lang: MonthLang, eventId?: string | null, upcoming?: boolean) => post<{ name: string; base64: string; events: number }>(monthPath(month, 'press'), { what: 'printLarge', lang, ...scope(eventId, upcoming) });

/** Email the month (or one event) to one tenant: queued to their own address, in their language. */
export const emailMonthReq = (month: string, tenantId: string, eventId?: string | null, upcoming?: boolean) => post<MonthEmailResult>(monthPath(month, 'press'), { what: 'email', tenantId, ...scope(eventId, upcoming) });

/** Find the tenant by name or by the whole apartment — and whether the list can be emailed to them. */
export function useMonthPeople(month: string, q: string) {
  const query = q.trim();
  return useQuery({ queryKey: ['events-month-people', month, query], queryFn: () => apiFetch<{ people: MonthPerson[] }>(`${monthPath(month, 'people')}?q=${encodeURIComponent(query)}`), enabled: query.length >= 2, staleTime: 5_000 });
}

/** "Sent to 703 · Oct 9": the month's own lines, newest first. */
export function useMonthSent(month: string, enabled: boolean) {
  return useQuery({ queryKey: ['events-month-sent', month], queryFn: () => apiFetch<{ sent: MonthSent[] }>(monthPath(month, 'sent')), enabled, staleTime: 20_000 });
}
